import React, { useState, useEffect } from 'react';
import { Box, Paper, IconButton, Typography, CircularProgress, Tooltip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';

export default function PdfSideViewer({ fileUrl, totalPages, initialPage = 6, onNumPages, loading }) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [numPages, setNumPages] = useState(totalPages || 0);
  const [scale, setScale] = useState(1.4);
  const [viewMode, setViewMode] = useState('scroll');

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    if (onNumPages) onNumPages(numPages);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(numPages, prev + 1));
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(2, prev + 0.2));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(0.4, prev - 0.2));
  };

  const handleViewModeChange = (_, newMode) => {
    if (newMode) setViewMode(newMode);
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header with controls */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0
      }}>
        <Typography variant="subtitle2" fontWeight="bold">
          PDF Preview
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
            <ToggleButton value="single">
              <Tooltip title="Single page">
                <MenuBookIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="scroll">
              <Tooltip title="Scroll">
                <ViewStreamIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Zoom out">
            <span>
              <IconButton
                onClick={handleZoomOut}
                disabled={scale <= 0.4}
                size="small"
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Typography variant="caption" sx={{ minWidth: 45, textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Typography>

          <Tooltip title="Zoom in">
            <span>
              <IconButton
                onClick={handleZoomIn}
                disabled={scale >= 2}
                size="small"
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Navigation controls */}
      {viewMode === 'single' && (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0
      }}>
        <Tooltip title="Previous page">
          <span>
            <IconButton
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Typography variant="body2" sx={{ mx: 2, minWidth: 80, textAlign: 'center' }}>
          {currentPage} / {numPages}
        </Typography>

        <Tooltip title="Next page">
          <span>
            <IconButton
              onClick={handleNextPage}
              disabled={currentPage >= numPages}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      )}

      {/* PDF Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: 'action.hover',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 3
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && fileUrl && (
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            }
            error={
              <Typography variant="body2" color="error" sx={{ p: 2 }}>
                Failed to load PDF
              </Typography>
            }
          >
            {/* All pages render up front so switching pages/modes is instant (no re-rasterizing) */}
            {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
              <Box
                key={pageNumber}
                sx={{
                  display: viewMode === 'scroll' || pageNumber === currentPage ? 'block' : 'none',
                  mb: viewMode === 'scroll' ? 2 : 0,
                }}
              >
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer={true}
                  renderAnnotationLayer={false}
                  scale={scale}
                  loading={
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  }
                />
              </Box>
            ))}
          </Document>
        )}
      </Box>
    </Paper>
  );
}
